import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ApiError } from "@/api/client";
import { Button, Dialog, TextField } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import { Decimal } from "@/lib/money";
import { recordOnlineRefund } from "@/repositories/localRefundRepository";
import { salesRepository, type Sale } from "@/repositories/salesRepository";

interface Chosen {
  quantity: string;
  restock: boolean;
}

/**
 * Giving money back.
 *
 * Built around a partial refund, because that is the ordinary case: somebody
 * buys six things and brings one back. Refunding the whole sale is the same
 * operation with every quantity at its maximum, so there is one screen rather
 * than two and no "full or partial?" question to answer first.
 *
 * **The stock decision is asked, never assumed.** A returned tin goes back on
 * the shelf; a returned bag of spoiled milk does not. Nothing in the software
 * can tell them apart, so the person holding the goods is made to say — and the
 * answer is recorded against the refund.
 *
 * What the dialog cannot do is name an amount. The figures come from the
 * original sale's own lines, so a line discounted at the time is given back at
 * what was actually charged.
 */
export function RefundDialog({
  sale,
  tillId,
  needsManager = false,
  onClose,
  onDone,
}: {
  sale: Sale;
  /** The till paying out. Its drawer and shift are the ones the money leaves. */
  tillId?: string | undefined;
  /** This person may not refund alone, so a manager approves from the start. */
  needsManager?: boolean;
  onClose: () => void;
  onDone: (refund: Sale) => void;
}) {
  const [chosen, setChosen] = useState<Record<string, Chosen>>({});
  const [reason, setReason] = useState("");
  // Shown only once the server says this shop wants a manager to approve, so a
  // shop that does not ask never sees the fields.
  const [approverCode, setApproverCode] = useState("");
  const [approverPin, setApproverPin] = useState("");
  // Minted once, when the dialog opens, and reused for every attempt. A key
  // generated inside the submit handler would be new on each retry, which is
  // precisely the bug idempotency exists to prevent — and this is money leaving
  // a drawer.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const refundable = useQuery({
    queryKey: ["sales", "refundable", sale.id],
    queryFn: () => salesRepository.refundable(sale.id),
  });

  // Memoised so the total below does not recompute on every render. Without it
  // the array is a new reference each time and the memo never holds.
  const lines = useMemo(
    () => refundable.data?.lines.filter((line) => Number(line.refundable) > 0) ?? [],
    [refundable.data],
  );

  const total = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const quantity = Number(chosen[line.line_id]?.quantity ?? 0);
        if (!Number.isFinite(quantity) || quantity <= 0) return sum;
        return sum.plus(new Decimal(line.unit_price).times(quantity));
      }, new Decimal(0)),
    [chosen, lines],
  );

  const refund = useMutation({
    mutationFn: () =>
      salesRepository.refund(
        sale.id,
        {
          lines: Object.entries(chosen)
            .filter(([, value]) => Number(value.quantity) > 0)
            .map(([line_id, value]) => ({
              line_id,
              quantity: String(value.quantity),
              restock: value.restock,
            })),
          reason: reason.trim(),
          ...(tillId ? { till_id: tillId } : {}),
          ...(approverCode && approverPin
            ? { approver_staff_code: approverCode.trim().toUpperCase(), approver_pin: approverPin }
            : {}),
        },
        idempotencyKey,
      ),
    onSuccess: (result) => {
      // If this till rang the sale up, tell its offline refund list.
      const numbers = new Map(lines.map((line) => [line.line_id, line.line_number]));
      void recordOnlineRefund({
        saleId: sale.id,
        total: total.toFixed(2),
        reason: reason.trim(),
        lines: Object.entries(chosen)
          .filter(([lineId, value]) => Number(value.quantity) > 0 && numbers.get(lineId) != null)
          .map(([lineId, value]) => ({
            lineNumber: numbers.get(lineId)!,
            quantity: Number(value.quantity),
          })),
      }).catch(() => {});
      onDone(result);
    },
    // A wrong PIN must not stay in the field for the next person to see.
    onError: () => setApproverPin(""),
  });

  const needsApproval =
    needsManager ||
    (refund.error instanceof ApiError && refund.error.code === "REFUND_APPROVAL_REQUIRED");

  const anything = Object.values(chosen).some((value) => Number(value.quantity) > 0);
  const valid =
    anything &&
    reason.trim().length > 0 &&
    (!needsApproval || (approverCode.trim().length > 0 && approverPin.length >= 4));

  function setQuantity(lineId: string, quantity: string, max: string) {
    const capped = Math.min(Number(quantity) || 0, Number(max));
    setChosen((current) => ({
      ...current,
      [lineId]: {
        quantity: capped > 0 ? String(capped) : "",
        restock: current[lineId]?.restock ?? true,
      },
    }));
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Refund ${sale.receipt_number}`}
      description="Choose what is coming back, and say whether it goes on the shelf again."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={refund.isPending}
            disabled={!valid}
            onClick={() => refund.mutate()}
          >
            Refund {formatMoney(total.toFixed(2))}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {lines.length === 0 ? (
          <p className="text-sm text-ink-600">
            Everything on this sale has already been given back.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {lines.map((line) => {
              const value = chosen[line.line_id];
              const quantity = Number(value?.quantity ?? 0);

              return (
                <li key={line.line_id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-ink-900">
                      {line.description}
                    </span>
                    <span className="block text-xs text-ink-500">
                      {formatMoney(line.unit_price)} each · {line.refundable} left to give back
                    </span>
                  </span>

                  <label className="flex items-center gap-2">
                    <span className="sr-only">How many of {line.description}</span>
                    <input
                      type="number"
                      min="0"
                      max={line.refundable}
                      step="1"
                      inputMode="numeric"
                      value={value?.quantity ?? ""}
                      onChange={(event) =>
                        setQuantity(line.line_id, event.target.value, line.refundable)
                      }
                      className="h-9 w-20 rounded-control border border-line bg-surface px-2 text-sm tabular-nums text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setQuantity(line.line_id, line.refundable, line.refundable)}
                    >
                      All
                    </Button>
                  </label>

                  {quantity > 0 && (
                    <label
                      className={cn(
                        "flex items-center gap-2 rounded-control border px-2.5 py-1.5 text-xs",
                        value?.restock !== false
                          ? "border-line text-ink-600"
                          : "border-warn-500/30 bg-warn-50 text-warn-700",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={value?.restock !== false}
                        onChange={(event) =>
                          setChosen((current) => ({
                            ...current,
                            [line.line_id]: {
                              quantity: current[line.line_id]?.quantity ?? "",
                              restock: event.target.checked,
                            },
                          }))
                        }
                        className="size-3.5 rounded border-line text-brand-500 focus:ring-brand-500/20"
                      />
                      {value?.restock !== false ? "Back on the shelf" : "Written off"}
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <TextField
          label="Why is it coming back?"
          value={reason}
          maxLength={300}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Customer said it was flat"
          hint="Recorded against the refund, with your name."
        />

        {needsApproval && (
          <div className="grid gap-3 rounded-control bg-warn-50 p-3 sm:grid-cols-2">
            <p className="text-sm text-warn-700 sm:col-span-2">
              A manager approves this refund. They enter their own staff code and PIN here.
            </p>
            <TextField
              label="Manager's staff code"
              value={approverCode}
              autoComplete="off"
              onChange={(event) => setApproverCode(event.target.value.toUpperCase())}
            />
            <TextField
              label="Manager's PIN"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              value={approverPin}
              onChange={(event) => setApproverPin(event.target.value.replace(/\D/g, ""))}
            />
          </div>
        )}

        {refund.error != null && (
          <p className="text-sm text-danger-700">
            {refund.error instanceof Error ? refund.error.message : "That refund was refused."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
